import { GridSortDirection } from '@mui/x-data-grid/models/gridSortModel'

import {
  ACTION_CODE,
  BIND_TYPE,
  ENTITY_COMMAND_TYPE,
  EVENT_CODE_GANTT,
  FIELD_VALUE_TYPE,
  PINNED_COLUMN,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import {
  ColumnParametersType,
  CommandParametersType,
  EntityCommandDTO,
  GETDictionary,
  ModalType,
  ObjectFieldDTO,
  ObjectShortDTO,
  ShareColumnType,
} from '@types'

import { JS_FUN_ARGS } from '@gantt/constants'

import { GanttParametersFormType } from '../components/GanttConfiguration/components/GeneralForm/hooks/useGanttParametersForm'

export type ModelType = 'editModel' | 'newModel'

export enum SegmentType {
  SINGLE = 'single',
  OWN_SECTIONS = 'own-sections',
  REF_SECTIONS = 'ref-sections',
  MULTI = 'multi',
}

export enum SegmentFieldType {
  DATETIME_START = 'datetimeStart',
  DATETIME_END = 'datetimeEnd',
  TITLE = 'title',
  TOOLTIP = 'tooltip',
  EMPTY = 'empty',
}

export enum SORT_BINDING_TYPE {
  STATIC = 'static',
  JSON = 'json',
}

export enum FILTER_BINDING_TYPE {
  STATIC = 'static',
  // JSON = 'json',
}

export enum TITLE_BINDING_TYPE {
  FIELD = 'field',
  JS = 'js',
  STATIC = 'static',
  FIELD_ARRAY = 'field_array',
}

export enum RESOURCE_COLUMN_FIELD_BINDING_TYPE {
  FIELD = 'field',
  JS = 'js',
  STATIC = 'static',
  // FIELD_ARRAY = 'field_array',
}

export enum TOOLTIP_BINDING_TYPE {
  FIELD = 'field',
  JS = 'js',
  STATIC = 'static',
  FIELD_ARRAY = 'field_array',
  JSON = 'json',
}

export enum BACKGROUND_BINDING_TYPE {
  STATIC = 'static',
  JS = 'js',
}

export enum BIND_TYPE_INPUTS_FORM_TYPE {
  RESOURCE = 'resource',
  TIMELINE = 'timeline',
}

export enum SORT_ORDER_TYPE {
  ASC = 'asc',
  DESC = 'desc',
}

export enum AXIS_TYPE {
  Y = 'Y',
  X = 'X',
}

export enum DATE_TIME_TYPE {
  FIELD = 'field',
  JS = 'js',
}

export enum MAX_DURATION_TYPE {
  STATIC = 'static',
  FIELD = 'field',
  JS = 'js',
}

export enum LINES_WIDTH_BINDING_TYPE {
  STATIC = 'static',
  JS = 'js',
}

export enum BORDERS_WIDTH_BINDING_TYPE {
  STATIC = 'static',
  JS = 'js',
}

export const enum UNIT_INTERVAL_OPTIONS {
  MINUTE = 'minute',
  QUARTER = 'quarter',
  HALF = 'half',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type GanttSegmentType = {
  id: number
  value: SegmentType
  name: string
}

export type AvailableVariables = Record<JS_FUN_ARGS, string[]>

export type PageContextType = {
  objects?: ObjectShortDTO[]
  showDialog: boolean
  modalType: ModalType
  bindingValuesColumns?: GETDictionary[]
  objectFields?: ObjectFieldDTO[]
  objectFieldsForFilter?: ObjectFieldDTO[]
  currentRow?: (GanttColumnForm & GanttActionType & SectionType) | null
  rawObjectFields?: ObjectFieldDTO[]
  objectCodeResource?: string
  objectCodeTimelines?: string
  bindingValuesActionResourceEvents: GETDictionary[]
  bindingValuesActionTimelineEvents: GETDictionary[]
  bindingValuesResourceAction?: GETDictionary[]
  bindingValuesTimelineAction?: GETDictionary[]
  ganttCode: string
  watchedObjectResource: AutocompleteOption<string> | null
  watchedObjectTimeline: AutocompleteOption<string> | null
  currentColumn: GanttColumnForm
  columnToDelete?: number | string
  currentConfiguration?: GanttFormValuesBack
  currentActionResource?: GanttActionType
  currentActionTimeline?: GanttActionType
  actionResourceToDelete?: string | number
  actionTimelineToDelete?: string | number
  GANTT_SEGMENT_TYPE_OPTIONS: GanttSegmentType[]
  currentColumnsList: GanttColumnForm[]
  currentSectionsList: SectionType[]
  currentSegment?: SectionType
  segmentToDelete?: string | number
  currentBarIndex: number
  currentBarObject?: AutocompleteOption<string> | null
  availableVariables: AvailableVariables
  currentActionResourceList: GanttActionType[]
  currentActionTimelineList: GanttActionType[]
  isSuccessCreated: boolean
  isGanttLoading: boolean
  existingPaths: string[]
  selectedPaths: string[]
} & ContextHandlers

type ContextHandlers = {
  handleOpenDialog: (props: {
    type: ModalType
    id?: number | string
    row?: GanttColumnForm | GanttActionType | SectionType | null
    barIndex?: number | null
    selectedBarObject?: AutocompleteOption<string> | null
    inputAvailableVariables?: AvailableVariables
  }) => void
  handleCloseModal: (isDirty?: boolean) => void
  handleEdit: (
    row: GanttColumnForm | GanttActionType | SectionType | null,
    id?: number | string,
    isAction?: boolean
  ) => void
  handleHide: (row: GanttColumnForm, checked: boolean) => void
  onSetModalProperty?: (type: ShareColumnType) => void
  onSetObjectCodeResource: (code: string) => void
  onSetObjectCodeTimelines: (code: string) => void
  addGantt: (gantt: GanttFormValues) => Promise<any>
  onSetWatchedObjectResource: (value: AutocompleteOption<string> | null) => void
  onSetWatchedObjectTimeline: (value: AutocompleteOption<string> | null) => void
  onSetCurrentColumn: (column: GanttColumnForm) => void
  onSetColumnToDelete: (id: number) => void
  onDeleteColumn: (id: string | number) => void
  onSetCurrentConfiguration: (configuration: GanttFormValuesBack) => void
  onSetCurrentActionResource: (action: GanttActionType) => void
  onSetCurrentActionTimeline: (action: GanttActionType) => void
  onSetActionToDeleteResource: (id: string | number) => void
  onSetActionToDeleteTimeline: (id: string | number, barIndex: number) => void
  onSetCurrentColumnsList: (value: GanttColumnForm[]) => void
  onSetCurrentSectionsList: (value: SectionType[]) => void
  onSetCurrentSegment: (value: SectionType) => void
  onSetSegmentToDelete: (id: string | number, barIndex: number) => void
  onSetCurrentBarIndex: (index: number) => void
  onSetCurrentBarObject: (barObject: AutocompleteOption<string> | null) => void
  onSetCurrentActionTimelineList: (action: GanttActionType[]) => void
  onSetCurrentActionResourceList: (action: GanttActionType[]) => void
  onSetIsSuccessCreated: (value: boolean) => void
}

export type EmbeddedObject = {
  objectCode: string
  valueType: FIELD_VALUE_TYPE
  refValueType: FIELD_VALUE_TYPE
  field: string
  isPk?: boolean
  isInternalId?: boolean
  description?: string
  model?: string
  type?: string
}

export type LinkType = {
  refObjTimelines: string
  refObjJobs: string
  embeddedObjectPickerTimelines: EmbeddedObject[]
  embeddedObjectPickerJobs: EmbeddedObject[]
}

export type AutocompleteOption<T = number | string> = {
  id: T
  label: string
  internalId?: T
}

export type ConfigField = {
  pathStr: string
  pathArray: EmbeddedObject[]
  field: string
  commands?: GanttFormCommands
  formatDate?: string
  comment?: string
  isSystem?: boolean
  pathResolved?: boolean
}

export type ConfigSort =
  | Array<{
      field: ConfigField
      order: GridSortDirection
    }>
  | undefined

export enum GANTT_BIND_TYPE_LIST {
  FIELD = 'field',
  JS = 'js',
  JSX = 'jsx',
  STATIC = 'static',
  FIELD_ARRAY = 'field_array',
  JSON = 'json',
  CUSTOM = 'custom',
  COMMANDS = 'commands',
  COMMAND = 'command',
  FORM = 'form',
  TOOLTIP = 'tooltip',
  CONTEXT_MENU = 'contextMenu',
  ICON = 'icon',
}

export type BindTypeField = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.FIELD
  field: ConfigField | undefined
}

export type BindTypeFieldArr = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.FIELD_ARRAY
  field_array: ConfigField[] | undefined
}

export type BindTypeStatic = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.STATIC
  static: string | number | boolean | null | undefined
}

export type BindTypeStaticBoolean = {
  bindType: GANTT_BIND_TYPE_LIST.STATIC
  static: boolean | undefined
}

export type BindTypeJs = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.JS
  js: string | undefined
}

export type BindTypeJsx = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.JSX
  jsx: string | undefined
}

export type BindTypeJson = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.JSON | null
  json: string | undefined
}

export type BindTypeCustom = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.CUSTOM
  custom: any
}

export type BindTypeCommand = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.COMMAND
  command: string
}

export type BindTypeCommands = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.COMMANDS
  commands: EntityCommandDTO[]
}

export type BindTypeForm = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.FORM
  form: AutocompleteOption<string>
}

export type BindTypeTooltip = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.TOOLTIP
  tooltip: AutocompleteOption<string>
}

export type BindTypeContextMenu = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.CONTEXT_MENU
  contextMenu: AutocompleteOption<string>
}

export type BindTypeIcon = {
  id?: string
  bindType: GANTT_BIND_TYPE_LIST.ICON
  icon: string | undefined
}

export enum CONTEXT_MENU_BIND_TYPE {
  JSON = 'json',
  JS = 'js',
}

export enum OPEN_MODAL_BIND_TYPE {
  JSON = 'json',
  CUSTOM = 'custom',
  FORM = 'form',
  TOOLTIP = 'tooltip',
  CONTEXT_MENU = 'contextMenu',
}

export type BindField =
  | BindTypeField
  | BindTypeFieldArr
  | BindTypeStatic
  | BindTypeJs
  | BindTypeJsx
  | BindTypeJson
  | BindTypeCustom
  | BindTypeCommand
  | BindTypeCommands
  | BindTypeForm
  | BindTypeTooltip
  | BindTypeContextMenu
  | BindTypeIcon

export type GanttFormCommands = Array<{
  httpMethod: string | null
  type: AutocompleteOption<ENTITY_COMMAND_TYPE> | null
  name: AutocompleteOption<string> | null
  sourceType: AutocompleteOption<TYPES_SOURCE_OPTIONS> | null
  sourceCommand: AutocompleteOption<string> | null
  parameters: CommandParametersType[]
  body: CommandParametersType[]
}>

export type SectionType = {
  id: string | number
  title: BindField
  titles: BindField[]
  tooltip: BindField
  datetimeStart: BindField
  datetimeEnd: BindField
  duration?: BindField
  maxDuration?: BindField
  order?: number
  link: {
    axisX: BindTypeField
    axisY: BindTypeField
    systemName: string
    resourceKeyField: string
    resourceLinkField: string
  }
  background: BindTypeStatic | BindTypeJs
  labels: {
    leftTopTitle: BindField
    topTitle: BindField
    rightTopTitle: BindField
    leftBottomTitle: BindField
    bottomTitle: BindField
    rightBottomTitle: BindField
  }
  icons: {
    leftTopIcon: BindField
    topIcon: BindField
    rightTopIcon: BindField
    leftIcon: BindField
    centerIcon: BindField
    rightIcon: BindField
    leftBottomIcon: BindField
    bottomIcon: BindField
    rightBottomIcon: BindField
  }
  lines: {
    top: {
      has: BindTypeStaticBoolean | BindTypeJs
    }
    bottom: {
      has: BindTypeStaticBoolean | BindTypeJs
    }
  }
  activeSegment: {
    has: BindTypeStaticBoolean | BindTypeJs
    color: BindTypeStatic | BindTypeJs
    opacity: string
    verticalMargin: BindTypeStatic | BindTypeJs
    sideMargin: BindTypeStatic | BindTypeJs
  }
  textColor: BindTypeStatic | BindTypeJs
  textSettings?: {
    useTextSettings: boolean
    fontFamily: string
    fontSize: string
    isBold: boolean
    isItalic: boolean
    alignment: string
    fontColor: string
  }
  segmentHeight: BindField
}

export type TimelineItem = {
  data: AutocompleteOption<string> | null
  isMergeSegments: boolean
  segmentType: SegmentType
  sections: SectionType[]
  key: BindTypeField
  primaryKey: string
  additionalFields: ConfigField[]
  commandVariables: ConfigField[]
  tooltipVariables: ConfigField[]
  contextMenuVariables: ConfigField[]
  actions: GanttActionType[]
  hasCorners: boolean
  segmentHeight: BindField
  title: BindField
  titles: BindField[]
  tooltip: BindField
  background: BindTypeStatic | BindTypeJs
  filter: BindTypeStatic | BindTypeJson
  timeEdgesFilter: BindTypeStatic | BindTypeJson
  contextMenu?: ContextMenuAction[]
  commands: GanttFormCommands
  lines: {
    top: {
      has: BindTypeStaticBoolean | BindTypeJs
      color: BindTypeStatic | BindTypeJs
      width: BindTypeStatic | BindTypeJs
    }
    bottom: {
      has: BindTypeStaticBoolean | BindTypeJs
      color: BindTypeStatic | BindTypeJs
      width: BindTypeStatic | BindTypeJs
    }
  }
  borders: {
    inner: {
      has: BindTypeStaticBoolean | BindTypeJs
      color: BindTypeStatic | BindTypeJs
      width: BindTypeStatic | BindTypeJs
    }
    outer: {
      has: BindTypeStaticBoolean | BindTypeJs
      color: BindTypeStatic | BindTypeJs
      width: BindTypeStatic | BindTypeJs
    }
  }
  activeSegment: {
    has: BindTypeStaticBoolean | BindTypeJs
    color: BindTypeStatic | BindTypeJs
    opacity: string
    verticalMargin: BindTypeStatic | BindTypeJs
    sideMargin: BindTypeStatic | BindTypeJs
  }
  textColor: BindTypeStatic | BindTypeJs
  textSettings?: {
    fontFamily: string
    fontSize: string
    isBold: boolean
    isItalic: boolean
    alignment: string
    fontColor: string
  }
  boxVerticalGuideLines: GuideLines
  isNew?: boolean // поле для выставления флага isNewBar в TimelineForm для выбора объекта бара, на бэк не отправляется
}

export type ResourceItem = {
  additionalFields: ConfigField[]
  commandVariables: ConfigField[]
  tooltipVariables: ConfigField[]
  contextMenuVariables: ConfigField[]
  data: AutocompleteOption<string> | null
  key: BindTypeField
  primaryKey: string
  columns: GanttColumnForm[]
  actions: GanttActionType[]
  filter: BindTypeStatic | BindTypeJson
  timeEdgesFilter: BindTypeStatic | BindTypeJson
  commands: GanttFormCommands
}

export type GanttColumnForm = {
  id: number | string
  code: string
  title: string
  order?: string | number
  pinning: boolean
  useParameters: boolean
  pinnedColumn: PINNED_COLUMN | null
  visibility: boolean
  parameters: ColumnParametersType
  useLocalParameters?: boolean
  fields: BindField[]
  field?: BindTypeField
  bindType?: BIND_TYPE | AutocompleteOption
  jsValue?: string
  objectValue?: string
  valueType?: FIELD_VALUE_TYPE | null
  formatDate?: AutocompleteOption | string | null
}

export type GanttActionTargetType =
  | string[]
  | { id: unknown; label: unknown }[]
  | string //old configs
  | { id: unknown; label: unknown } //old configs
  | undefined

export type GanttActionType = {
  id: number | string
  code: string
  title: string
  actionCode: ACTION_CODE
  eventCode: EVENT_CODE_GANTT
  target?: GanttActionTargetType
  actionField: BindField
}

export type ContextMenuAction = {
  id: number | string
  name: string
  additionalData?: {
    order?: GridSortDirection
    statusCode?: string
  }
  command?: string
  commands?: EntityCommandDTO[]
  isHeader?: boolean
  isDivider?: boolean
}
export type GuideLines = {
  has: BindTypeStaticBoolean | BindTypeJs
  isBoxColor: BindTypeStaticBoolean | BindTypeJs
  color: BindTypeStatic | BindTypeJs
  width: BindTypeStatic | BindTypeJs
  type: BindTypeStatic | BindTypeJs
}

export type GridLineParams = {
  type: 'dashed' | 'solid'
  displayValue: boolean
  color: BindTypeStatic
  width: number
  spacing: number
  opacity: number
}

export type GanttFormValues = {
  id?: number | string
  viewId?: number | string
  code: string
  title: string
  canvas: GanttParametersFormType
  resource: ResourceItem
  timeline: TimelineItem[]
  sort: {
    axis: AXIS_TYPE
    code: string
    value: {
      field: string | null
      order: SORT_ORDER_TYPE | null
    }[]
  }
}

export enum GanttPlanningMode {
  ROW = 'row',
  GROUPS = 'groups',
}

export type GanttCopyDataPayload = {
  id: number
  code: string
  title: string
}

export type GanttFormValuesBack = Omit<GanttFormValues, 'resource' | 'timeline'> & {
  resource: Omit<ResourceItem, 'commands'> & {
    commands: EntityCommandDTO[]
  }
  timeline: (Omit<TimelineItem, 'commands'> & { commands: EntityCommandDTO[] })[]
}

export type GanttConfigForSave = Omit<GanttFormValuesBack, 'resourceTimelineObjects'>
